/**
 * Baseten scaling surfaces for a dedicated-fleet model (TRA-1051), rendered
 * only when the model's primary route forwards to the `baseten` upstream:
 *
 *   - ReplicaCountBadge: the live replica count in the page header, next to
 *     the serving badge.
 *   - BasetenScalingSection: the "Replicas over time" band — a step-line
 *     chart of the gateway's 2-minute samples with a window toggle plus a
 *     custom window box (1h to 7d, TRA-1436), a Wake button while the
 *     deployment is scaled to zero, and a pencil popover holding every
 *     Baseten autoscaling setting (TRA-1275).
 *   - ReplicaUsageChart: that chart with the busy part under the Running
 *     line filled with diagonal stripes (TRA-1437): the requests running
 *     at each instant (Baseten's own 2-minute count, or the 15-minute
 *     usage cell's mean where the gateway has none) over the concurrency
 *     target in force, capped at the replicas running. A stretch where the load
 *     reached every running replica's target wears red stripes, because
 *     requests waited there, and a thin line along the top of the
 *     stripes in their colour. Drag across it to zoom into a stretch; the
 *     metrics page's capacity band draws the same one at half the height,
 *     zoomed and moved by the page around it (TRA-1592).
 *     A small dot on the time line marks each saved replica setting change
 *     inside the stretch shown (TRA-1558), with when, who and what on hover.
 *     On the model page two arrows on the chart's edges move the stretch
 *     shown to earlier or later times by half its width (useReplicaView,
 *     replicaPan). The reads only take hours back from now, so moving back
 *     loads a longer window, up to the 7 days the gateway keeps, and draws
 *     the part being looked at; the old picture stays while it loads.
 *
 * Scaling state is read live from the Baseten management API through the
 * gateway (refetched every 60s); writes go back the same path.
 *
 * The settings in the popover are the model's REGULAR ones: what applies
 * outside every schedule window (BasetenScheduleSection, TRA-1426). While a
 * window is in force the replicas on the chart follow the window, and the
 * popover still shows and saves the regular settings, so a save can never
 * copy a window's range into them.
 */

import { useId, useState } from 'react'
import { CircleHelp, Pencil } from 'lucide-react'
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceDot,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  type DotProps,
} from 'recharts'
import { toast } from 'sonner'

import type { ModelScalingResponse, ReplicaSettingChangeResponse } from '@/hooks/useModels'
import {
  CHART_CHANGES_LIMIT,
  useModelScaling,
  useModelScalingChanges,
  useModelScalingUsage,
  useModelSchedules,
  useUpdateModelScaling,
  useWakeModel,
} from '@/hooks/useModels'
import { useTeam } from '@/contexts/TeamContext'
import { ModelHomeSection } from '@/components/models/home/ModelHomeSection'
import {
  LEGEND_LABELS,
  STRIPE_KEYS,
  chartPeak,
  hoverLines,
  mergeSeries,
  stripeSeries,
  type ReplicaChartRow,
  type StripedRow,
} from '@/components/models/home/replicaUsage'
import { changeMarks, marksCapped } from '@/components/models/home/replicaMarks'
import {
  ChartControls,
  Note,
  type PagePan,
  PanArrow,
  ResetZoomButton,
  ShownLine,
} from '@/components/models/home/PanControls'
import { loadedRange, zoomRows, type TimeRange } from '@/components/models/home/replicaZoom'
import { MAX_WINDOW_HOURS, formatSpanTime } from '@/components/models/home/scalingWindow'
import { LineMark } from '@/components/models/home/ThroughputChart'
import { useDragZoom, dragShade } from '@/components/models/home/useDragZoom'
import { useLastDefined } from '@/hooks/useLastDefined'
import { useReplicaView, type ReplicaView } from '@/components/models/home/useReplicaView'
import { useWindowBox } from '@/components/models/home/useWindowBox'
import {
  WindowBoxError,
  WindowPicker,
  type WindowOption,
} from '@/components/models/home/WindowPicker'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Tooltip as HelpTooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'

const STATUS_LABELS: Record<string, string> = {
  ACTIVE: 'Active',
  SCALED_TO_ZERO: 'Scaled to zero',
  WAKING_UP: 'Waking up',
  DEPLOYING: 'Deploying',
  BUILDING: 'Building',
  LOADING_MODEL: 'Loading model',
  UPDATING: 'Updating',
  UNHEALTHY: 'Unhealthy',
  INACTIVE: 'Inactive',
  FAILED: 'Failed',
}

/**
 * The window pills. A pill's value is its hours, so a custom window typed
 * in the box beside them (TRA-1436) lights the pill with the same hours, or
 * none when no pill has them.
 */
const SCALING_WINDOWS: WindowOption[] = [
  { value: '1', label: '1h' },
  { value: '6', label: '6h' },
  { value: '24', label: '24h' },
  { value: '72', label: '3d' },
  { value: '168', label: '7d' },
]

/**
 * The section's default window; the header badge shares its query. 24 hours
 * and not 7 days because the gateway's 7-day usage read takes about 10 s on a
 * first load of a model page, the 24-hour one about 1 s (measured 2026-10-16).
 */
const DEFAULT_WINDOW_HOURS = 24

const CUSTOM_WINDOW_HELP = 'Enter 1h to 7d, for example 36h or 3d.'
/** Links the error line to the box, so a screen reader reads it with the box. */
const CUSTOM_WINDOW_ERROR_ID = 'replica-window-error'

const RUNNING_STROKE = 'hsl(var(--foreground))'
/** The busy stripes: a calm blue from the chart palette. */
const USED_FILL = 'hsl(var(--chart-1))'
/** The full stripes: the palette's muted red, the p99 colour on the metrics page. */
const FULL_FILL = 'hsl(var(--chart-4))'

/** Diagonal stripes in one colour, for an area fill or a legend swatch. */
function StripePattern({ id, color }: { id: string; color: string }): React.JSX.Element {
  return (
    <pattern id={id} patternUnits="userSpaceOnUse" width="6" height="6" patternTransform="rotate(45)">
      <rect width="2" height="6" fill={color} fillOpacity={0.7} />
    </pattern>
  )
}

/** An id safe inside `url(#...)`: React's ids carry punctuation. */
function usePatternId(kind: string): string {
  return `${useId().replace(/[^a-zA-Z0-9]/g, '')}-${kind}`
}

/** A legend swatch: a small box of the series' stripes. */
function StripeMark({ color }: { color: string }): React.JSX.Element {
  const id = usePatternId('mark')
  return (
    <svg width="12" height="8" aria-hidden>
      <defs>
        <StripePattern id={id} color={color} />
      </defs>
      <rect width="12" height="8" fill={`url(#${id})`} />
    </svg>
  )
}

/**
 * Live replica count for the page header. Quiet while the state loads or
 * fails, same stance as TraceLoggingBadge beside it.
 */
export function ReplicaCountBadge({
  modelId,
}: {
  modelId: string
}): React.JSX.Element | null {
  const scaling = useModelScaling(modelId, true, DEFAULT_WINDOW_HOURS)
  if (scaling.data === undefined) return null
  const count = scaling.data.active_replicas
  // Baseten can omit the count from the deployment object; a chip with no
  // number would be noise, so stay quiet like the loading state.
  if (count === null) return null
  return (
    <Badge
      variant="outline"
      title={STATUS_LABELS[scaling.data.status] ?? scaling.data.status}
    >
      {count} {count === 1 ? 'replica' : 'replicas'}
    </Badge>
  )
}

function ReplicaTooltip({
  active,
  payload,
  span,
}: {
  active?: boolean
  payload?: readonly { payload?: unknown }[]
  span: TimeRange
}): React.JSX.Element | null {
  const row = payload?.[0]?.payload as ReplicaChartRow | undefined
  if (!active || !row) return null
  const [running, full] = hoverLines(row)
  return (
    <div className="border border-border bg-card p-2.5 text-xs">
      <p className="font-mono uppercase tracking-wider text-muted-foreground">
        {formatSpanTime(row.epoch, span)}
      </p>
      <p className="mt-1 font-mono tabular-nums">
        {running}
        {row.status !== null && (
          <span className="text-muted-foreground">
            {' · '}
            {STATUS_LABELS[row.status] ?? row.status}
          </span>
        )}
      </p>
      {full !== undefined && <p>{full}</p>}
    </div>
  )
}

function ChartLegend(): React.JSX.Element {
  return (
    <div className="mt-2 flex flex-wrap items-center gap-4 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
      <span className="flex items-center gap-1.5">
        <LineMark stroke={RUNNING_STROKE} dash={undefined} />
        {LEGEND_LABELS.running}
      </span>
      <span className="flex items-center gap-1.5">
        <StripeMark color={USED_FILL} />
        {LEGEND_LABELS.used}
      </span>
      <span className="flex items-center gap-1.5">
        <StripeMark color={FULL_FILL} />
        {LEGEND_LABELS.full}
      </span>
    </div>
  )
}

/**
 * One saved replica setting change on the time line (TRA-1558): a dot in
 * the Running line's colour, with its words in a native hover title.
 * recharts hands the dot its place; `title` is the mark's.
 */
function ChangeDot({ cx, cy, title }: DotProps & { title: string }): React.JSX.Element {
  return (
    <circle
      cx={cx}
      cy={cy}
      r={3.5}
      fill={RUNNING_STROKE}
      stroke="hsl(var(--card))"
      strokeWidth={1}
      style={{ pointerEvents: 'all' }}
    >
      <title>{title}</title>
    </circle>
  )
}

/** The arrows on the chart's edges. */
function EdgeArrows({ view }: { view: ReplicaView }): React.JSX.Element {
  const edge = 'absolute top-1/2 z-10 -translate-y-1/2'
  return (
    <>
      <PanArrow
        direction="earlier"
        disabled={!view.canEarlier}
        onClick={view.earlier}
        className={`${edge} left-1`}
      />
      <PanArrow
        direction="later"
        disabled={!view.canLater}
        onClick={view.later}
        className={`${edge} right-1`}
      />
    </>
  )
}

/** Why the chart has nothing to draw, or null when it has. */
function nothingToDraw(loaded: TimeRange | null, rowCount: number): string | null {
  if (loaded === null) {
    return 'No samples in this window. The gateway records replica counts every 2 minutes.'
  }
  return rowCount === 0 ? 'No samples in this stretch.' : null
}

/**
 * The chart's frame: on the model page the Reset zoom button and the
 * arrows on its edges, around the plot or, when there is nothing to draw,
 * a line that says so. The controls stay through a stretch with no
 * samples, so a gap in the data can be moved across. Zoomed or moved, only
 * the rows inside the stretch shown draw (zoomRows), at full detail, over
 * an axis that spans the whole stretch, so a part not loaded yet stays
 * blank.
 */
function ReplicaHistoryChart({
  rows: all,
  changes,
  view,
  tall,
  arrows,
  pagePan,
}: {
  rows: readonly ReplicaChartRow[]
  /** The model's saved replica setting changes; the ones in the stretch shown get a dot. */
  changes: readonly ReplicaSettingChangeResponse[]
  view: ReplicaView
  tall: boolean
  arrows: boolean
  /** On the metrics page: the page's arrows and Reset zoom, shown like every other chart's. */
  pagePan?: PagePan
}): React.JSX.Element {
  const loaded = loadedRange(all)
  const span = view.shown ?? loaded
  const rows = stripeSeries(view.shown === null ? all : zoomRows(all, view.shown))
  const empty = nothingToDraw(loaded, rows.length)
  return (
    <div className={`group relative ${tall ? 'h-56' : 'h-28'} w-full select-none`}>
      {pagePan !== undefined && <ChartControls pan={pagePan} />}
      {arrows && view.zoomed && (
        <ResetZoomButton onClick={view.resetZoom} className="absolute right-2 top-0 z-10 h-6" />
      )}
      {arrows && <EdgeArrows view={view} />}
      {span !== null && empty === null ? (
        <ReplicaPlot rows={rows} span={span} changes={changes} onZoom={view.zoom} />
      ) : (
        <p className="absolute inset-0 flex items-center justify-center px-10 text-center text-sm text-muted-foreground">
          {empty}
        </p>
      )}
    </div>
  )
}

/**
 * The step line of replica samples, with the busy part under it striped
 * when the usage is there. The samples come every 2 minutes and the load
 * steps on their own clock, so the rows are the union of both (mergeSeries); the
 * stripes are stepped areas over the same rows (stripeSeries), two per
 * colour so a stretch never bleeds into the next, each with a thin line
 * along its top edge in its own colour. The top of the y axis follows the
 * rows, and the change marks follow the span (changeMarks).
 */
function ReplicaPlot({
  rows,
  span,
  changes,
  onZoom,
}: {
  rows: readonly StripedRow[]
  span: TimeRange
  changes: readonly ReplicaSettingChangeResponse[]
  onZoom: (zoom: TimeRange) => void
}): React.JSX.Element {
  const usedId = usePatternId('used')
  const fullId = usePatternId('full')
  const { drag, handlers } = useDragZoom(onZoom)
  const peak = chartPeak(rows)
  const yTicks = Array.from({ length: peak + 1 }, (_, i) => i)
  const marks = changeMarks(changes, span)
  return (
    <ResponsiveContainer width="100%" height="100%" minWidth={0}>
      <ComposedChart data={rows} margin={{ top: 8, right: 8, bottom: 0, left: 0 }} {...handlers}>
        <defs>
          <StripePattern id={usedId} color={USED_FILL} />
          <StripePattern id={fullId} color={FULL_FILL} />
        </defs>
        <CartesianGrid stroke="hsl(var(--border) / 0.6)" vertical={false} />
        <XAxis
          dataKey="epoch"
          type="number"
          domain={[span.start, span.end]}
          tickLine={false}
          axisLine={false}
          tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
          tickFormatter={(epoch: number) => formatSpanTime(epoch, span)}
        />
        <YAxis
          domain={[0, peak]}
          ticks={yTicks}
          allowDecimals={false}
          tickLine={false}
          axisLine={false}
          width={28}
          tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
        />
        <Tooltip
          content={({ active, payload }) => (
            <ReplicaTooltip active={active} payload={payload} span={span} />
          )}
          cursor={{
            stroke: 'hsl(var(--muted-foreground) / 0.4)',
            strokeDasharray: '3 3',
          }}
        />
        {/* The stripes go first so the Running line draws over them. A
            row whose use is unknown leaves a gap, not a zero. The stroke
            is the top edge only: recharts strokes an area's curve, not
            its base, and a run ends flat on the row the next one starts
            on, so the colours meet with no gap and no doubled line. */}
        {[
          ...STRIPE_KEYS.used.map((key) => ({ key, fill: usedId, stroke: USED_FILL })),
          ...STRIPE_KEYS.full.map((key) => ({ key, fill: fullId, stroke: FULL_FILL })),
        ].map(({ key, fill, stroke }) => (
          <Area
            key={key}
            type="stepAfter"
            dataKey={key}
            fill={`url(#${fill})`}
            stroke={stroke}
            strokeWidth={1.25}
            baseValue={0}
            dot={false}
            activeDot={false}
            connectNulls={false}
            isAnimationActive={false}
          />
        ))}
        {/* Replica count is a level that holds between samples, so the
            line steps rather than interpolating diagonals. */}
        <Line
          type="stepAfter"
          dataKey="replicas"
          stroke={RUNNING_STROKE}
          strokeWidth={1.5}
          dot={false}
          isAnimationActive={false}
        />
        {/* The change marks sit on the time line, under the levels. */}
        {marks.map((mark) => (
          <ReferenceDot
            key={mark.id}
            x={mark.epoch}
            y={0}
            shape={(props: DotProps) => <ChangeDot {...props} title={mark.title} />}
          />
        ))}
        {dragShade(drag)}
      </ComposedChart>
    </ResponsiveContainer>
)
}

/** A read's state as the chart tells it: still loading, or failed. */
export interface ReadState {
  loading: boolean
  failed: boolean
}

/**
 * The chart with everything around it: the arrows, the legend, and the
 * lines that say what is shown and what is still loading. No reads here,
 * so a test can render it in any state.
 */
export function ReplicaChartBody({
  rows,
  changes,
  view,
  tall,
  arrows,
  usageLoaded,
  usageFailed,
  earlier,
  pagePan,
}: {
  rows: readonly ReplicaChartRow[]
  changes: readonly ReplicaSettingChangeResponse[]
  view: ReplicaView
  tall: boolean
  /** The chart carries its own controls: the arrows, Reset zoom, the zoom hint and the stretch line. */
  arrows: boolean
  /** The usage read answered, so the stripes and their legend mean something. */
  usageLoaded: boolean
  usageFailed: boolean
  /** The longer window a move back asked for. */
  earlier: ReadState
  /** On the metrics page: the page's arrows and Reset zoom on the chart. */
  pagePan?: PagePan
}): React.JSX.Element {
  const span = view.shown ?? loadedRange(rows)
  // The route hands out its newest CHART_CHANGES_LIMIT rows at most. When
  // the stretch shown reaches further back than those, the chart says so
  // instead of showing a quiet gap.
  const capped = span !== null && marksCapped(changes, CHART_CHANGES_LIMIT, span)
  return (
    <div>
      <ReplicaHistoryChart
        rows={rows}
        changes={changes}
        view={view}
        tall={tall}
        arrows={arrows}
        pagePan={pagePan}
      />
      {usageLoaded && <ChartLegend />}
      {arrows && !view.zoomed && rows.length > 0 && <Note>Drag across the chart to zoom in.</Note>}
      {/* Without arrows the chart is zoomed and moved by the page around it, which says where it is. */}
      {arrows && view.moved && view.shown !== null && (
        <ShownLine shown={view.shown} onBackToNow={view.backToNow} />
      )}
      {earlier.loading && <Note>Loading earlier times...</Note>}
      {earlier.failed && <Note>Earlier times could not be loaded.</Note>}
      {usageFailed && <Note>Usage could not be loaded.</Note>}
      {capped && <Note>The chart marks the newest {CHART_CHANGES_LIMIT} changes only.</Note>}
    </div>
  )
}

/**
 * The replica chart with the busy stripes on it (TRA-1437). The usage is
 * best effort: when it cannot be read, the replica line draws alone and
 * one muted line says so. The zoom and the moves are time ranges, so a
 * usage refresh or a scaling refetch keeps them. `view` says where the
 * chart looks: its own state on the model page (HomeReplicaChart), the
 * page's on the metrics page, where one zoom moves every card.
 */
export function ReplicaUsageChart({
  modelId,
  view,
  tall = false,
  arrows = false,
  pagePan,
}: {
  modelId: string
  view: ReplicaView
  /** The model page doubles the chart; the metrics page keeps its cards level. */
  tall?: boolean
  /** The model page moves through time with controls on the chart; the metrics page moves the whole page. */
  arrows?: boolean
  /** On the metrics page: the page's arrows and Reset zoom, shown on the chart like on every other chart. */
  pagePan?: PagePan
}): React.JSX.Element {
  const scaling = useModelScaling(modelId, true, view.loadHours)
  const usage = useModelScalingUsage(modelId, true, view.loadHours)
  // The marks are best effort too: none while the changes load or fail.
  const changes = useModelScalingChanges(modelId, true, CHART_CHANGES_LIMIT)
  // A move back asks the reads for a longer window, and until that
  // answers, or when it fails, the chart keeps the old picture instead of
  // going blank; the lines under it say which.
  const history = useLastDefined(scaling.data?.history)
  const data = useLastDefined(usage.data)
  // At now this is the caller's own scaling read, which it waits for before
  // it draws the chart, so this skeleton shows only for a caller that did not.
  if (history === undefined) return <Skeleton className={`${tall ? 'h-56' : 'h-28'} w-full`} />
  return (
    <ReplicaChartBody
      rows={mergeSeries(history, data ?? null)}
      changes={changes.data ?? []}
      view={view}
      tall={tall}
      arrows={arrows}
      pagePan={pagePan}
      usageLoaded={data !== undefined}
      usageFailed={usage.isError}
      earlier={{
        loading: view.moved && (scaling.isLoading || usage.isLoading),
        failed: view.moved && (scaling.isError || usage.isError),
      }}
    />
  )
}

/**
 * The model page's chart, with its own zoom and arrows. The section keys
 * it by the window picked, so a new window mounts a fresh chart at now
 * with no zoom, even when its data is cached and arrives at once.
 */
function HomeReplicaChart({
  modelId,
  windowHours,
}: {
  modelId: string
  windowHours: number
}): React.JSX.Element {
  const view = useReplicaView(windowHours)
  return <ReplicaUsageChart modelId={modelId} view={view} tall arrows />
}

function WakeButton({ modelId }: { modelId: string }): React.JSX.Element {
  const mutation = useWakeModel()

  async function wake(): Promise<void> {
    await mutation.mutateAsync({ modelId })
    toast.success('Wake requested. The cold start takes a few minutes')
  }

  return (
    // Sized to sit flush with the h-6 window pills beside it.
    <Button
      variant="outline"
      size="sm"
      className="h-6 rounded-sm px-2.5 font-mono text-[10px] uppercase tracking-wider"
      onClick={() => { void wake() }}
      disabled={mutation.isPending}
    >
      {mutation.isPending ? 'Waking...' : 'Wake'}
    </Button>
  )
}

type KnobKey =
  | 'min_replica'
  | 'max_replica'
  | 'autoscaling_window'
  | 'scale_down_delay'
  | 'max_scale_down_rate'
  | 'concurrency_target'
  | 'target_utilization_percentage'

/**
 * Every Baseten autoscaling setting (TRA-1275), bounds per Baseten's docs.
 * `fallback` fills a knob Baseten omits from a read. Baseten leaves
 * max_scale_down_rate out until it has been written once, and its default
 * of 50 applies meanwhile, which is what the Baseten dashboard shows.
 */
interface Knob {
  key: KnobKey
  label: string
  /** Shown in the label's help tooltip. */
  description: string
  /** Appended to the allowed range in the tooltip; empty for a bare count. */
  unit: string
  min: number
  max: number
  fallback: number | null
}

const KNOBS: readonly Knob[] = [
  {
    key: 'min_replica',
    label: 'Min replicas',
    description:
      'The autoscaler never runs fewer replicas than this. Zero lets the deployment scale to zero when idle.',
    unit: '',
    min: 0,
    max: 64,
    fallback: null,
  },
  {
    key: 'max_replica',
    label: 'Max replicas',
    description: 'The autoscaler never runs more replicas than this.',
    unit: '',
    min: 1,
    max: 64,
    fallback: null,
  },
  {
    key: 'autoscaling_window',
    label: 'Autoscaling window (s)',
    description:
      'The autoscaler averages traffic over this many seconds when it decides to scale.',
    unit: ' seconds',
    min: 10,
    max: 3600,
    fallback: null,
  },
  {
    key: 'scale_down_delay',
    label: 'Scale-down delay (s)',
    description:
      'The autoscaler waits this many seconds after traffic drops before it removes replicas.',
    unit: ' seconds',
    min: 0,
    max: 3600,
    fallback: null,
  },
  {
    key: 'max_scale_down_rate',
    label: 'Max scale-down rate (%)',
    description:
      'The autoscaler removes at most this percentage of active replicas in one scale-down step. The Baseten default is 50.',
    unit: '%',
    min: 1,
    max: 50,
    fallback: 50,
  },
  {
    key: 'concurrency_target',
    label: 'Concurrency target',
    description:
      'Each replica handles this many concurrent requests before the autoscaler adds another.',
    unit: ' requests',
    min: 1,
    max: 1000,
    fallback: null,
  },
  {
    key: 'target_utilization_percentage',
    label: 'Target utilization (%)',
    description: 'Scaling triggers at this percentage of the concurrency target.',
    unit: '%',
    min: 1,
    max: 100,
    fallback: null,
  },
]

function KnobHelp({ knob }: { knob: Knob }): React.JSX.Element {
  return (
    <HelpTooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={`About ${knob.label}`}
          className="text-muted-foreground hover:text-foreground"
        >
          <CircleHelp className="h-3 w-3" />
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-64 text-xs">
        <p>{knob.description}</p>
        <p className="mt-1 text-muted-foreground">
          Allowed: {knob.min} to {knob.max}
          {knob.unit}.
        </p>
      </TooltipContent>
    </HelpTooltip>
  )
}

type KnobValues = Record<KnobKey, string>

function knobValuesFrom(scaling: ModelScalingResponse): KnobValues {
  const values = {} as KnobValues
  for (const knob of KNOBS) {
    const current = scaling[knob.key] ?? knob.fallback
    values[knob.key] = current === null ? '' : String(current)
  }
  return values
}

/** Empty is "leave as Baseten has it"; anything else must be an integer in range. */
function knobNumber(raw: string, min: number, max: number): number | null | undefined {
  if (raw.trim() === '') return null
  const n = Number(raw)
  return Number.isInteger(n) && n >= min && n <= max ? n : undefined
}

function AutoscalingForm({
  modelId,
  scaling,
  scheduled,
}: {
  modelId: string
  scaling: ModelScalingResponse
  /** The model has schedule windows, which override these settings while they run. */
  scheduled: boolean
}): React.JSX.Element {
  // Server-value changes (60s refetch, another tab's save) resync the form
  // via the `key` the parent derives from the current values, which
  // remounts this component; no effect needed.
  const mutation = useUpdateModelScaling()
  const initial = knobValuesFrom(scaling)
  const [values, setValues] = useState<KnobValues>(initial)

  const parsed = Object.fromEntries(
    KNOBS.map((k) => [k.key, knobNumber(values[k.key], k.min, k.max)]),
  ) as Record<KnobKey, number | null | undefined>
  const min = parsed.min_replica
  const max = parsed.max_replica
  const replicasValid = typeof min === 'number' && typeof max === 'number' && min <= max
  const outOfRange = KNOBS.find((k) => parsed[k.key] === undefined)
  const valid = replicasValid && outOfRange === undefined
  const dirty = KNOBS.some((k) => values[k.key] !== initial[k.key])

  /** Only knobs the user edited go in the PUT; the rest stay as Baseten has them. */
  function edited(key: KnobKey): number | null | undefined {
    return values[key] === initial[key] ? undefined : parsed[key]
  }

  async function save(): Promise<void> {
    if (typeof min !== 'number' || typeof max !== 'number') return
    await mutation.mutateAsync({
      modelId,
      data: {
        min_replica: min,
        max_replica: max,
        autoscaling_window: edited('autoscaling_window'),
        scale_down_delay: edited('scale_down_delay'),
        max_scale_down_rate: edited('max_scale_down_rate'),
        concurrency_target: edited('concurrency_target'),
        target_utilization_percentage: edited('target_utilization_percentage'),
      },
    })
    toast.success('Autoscaling updated')
  }

  return (
    <div className="space-y-3">
      <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
        Autoscaling
      </p>
      {scheduled && (
        <p className="max-w-[19rem] text-xs text-muted-foreground">
          These settings apply outside scheduled windows.
        </p>
      )}
      <div className="grid grid-cols-2 gap-3">
        {KNOBS.map((knob) => (
          <div key={knob.key} className="space-y-1">
            <div className="flex items-center gap-1">
              <Label
                htmlFor={`knob-${knob.key}`}
                className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground"
              >
                {knob.label}
              </Label>
              <KnobHelp knob={knob} />
            </div>
            <Input
              id={`knob-${knob.key}`}
              type="number"
              min={knob.min}
              max={knob.max}
              value={values[knob.key]}
              onChange={(e) => {
                setValues((prev) => ({ ...prev, [knob.key]: e.target.value }))
              }}
              className="w-36"
              disabled={mutation.isPending}
              aria-invalid={parsed[knob.key] === undefined}
            />
          </div>
        ))}
      </div>
      {outOfRange !== undefined ? (
        <p className="text-sm text-destructive">
          {outOfRange.label} must be a whole number from {outOfRange.min} to{' '}
          {outOfRange.max}.
        </p>
      ) : !replicasValid ? (
        <p className="text-sm text-destructive">
          Min replicas cannot exceed max replicas.
        </p>
      ) : null}
      <Button
        onClick={() => { void save() }}
        disabled={!valid || !dirty || mutation.isPending}
      >
        {mutation.isPending ? 'Saving...' : 'Save'}
      </Button>
    </div>
  )
}

/** The "Replicas over time" band: chart + window toggle + custom box + wake + edit. */
export function BasetenScalingSection({
  modelId,
}: {
  modelId: string
}): React.JSX.Element {
  const { isRestricted } = useTeam()
  const [windowHours, setWindowHours] = useState(DEFAULT_WINDOW_HOURS)
  // A bad value keeps the last good window on the chart and says so below it.
  const box = useWindowBox(MAX_WINDOW_HOURS, setWindowHours)
  const scaling = useModelScaling(modelId, true, windowHours)
  // Shares the schedule band's query; quiet while it loads or fails, since
  // all it adds here is one line of context in the popover.
  const schedules = useModelSchedules(modelId, true)
  const data = scaling.data

  return (
    <ModelHomeSection
      title="Replicas over time"
      action={
        <div className="flex items-center gap-3">
          <WindowPicker
            options={SCALING_WINDOWS}
            hours={windowHours}
            onChange={setWindowHours}
            box={box}
            pillsLabel="Replica history window"
            boxLabel="Custom replica history window"
            errorId={CUSTOM_WINDOW_ERROR_ID}
          />
          {data?.status === 'SCALED_TO_ZERO' && !isRestricted && (
            <WakeButton modelId={modelId} />
          )}
          {data !== undefined && !isRestricted && (
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-6 w-6 rounded-sm"
                  aria-label="Edit autoscaling"
                >
                  <Pencil className="h-2.5 w-2.5" />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-auto">
                <AutoscalingForm
                  key={JSON.stringify(knobValuesFrom(data))}
                  modelId={modelId}
                  scaling={data}
                  scheduled={(schedules.data?.schedules.length ?? 0) > 0}
                />
              </PopoverContent>
            </Popover>
          )}
        </div>
      }
    >
      {scaling.isError ? (
        <p className="text-sm text-destructive">Failed to load scaling state.</p>
      ) : data === undefined ? (
        <Skeleton className="h-56 w-full" />
      ) : (
        <HomeReplicaChart key={windowHours} modelId={modelId} windowHours={windowHours} />
      )}
      <WindowBoxError box={box} id={CUSTOM_WINDOW_ERROR_ID} help={CUSTOM_WINDOW_HELP} />
    </ModelHomeSection>
  )
}
